import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Stored visx tooltip data for when tooltip is pinned.
 * These are the values from visx's TooltipContext that we freeze.
 */
export interface PinnedVisxTooltipData {
  tooltipLeft: number;
  tooltipTop: number;
  tooltipData: object;
}

/**
 * Data stored when tooltip is pinned (for our own use, like crosshair position).
 */
export interface PinnedTooltipData {
  /** SVG x-coordinate where the crosshair should stay fixed */
  xPosition: number;
  /** Data x-value for the pinned data point (number for time/linear, string for categories) */
  xValue: number | string;
  /** Pre-rendered tooltip content to display when pinned */
  content: ReactNode;
  /** Visx tooltip data to freeze (tooltipLeft, tooltipTop, tooltipData) */
  visxData: PinnedVisxTooltipData;
}

/**
 * Context for tooltip interaction state.
 * Tracks which X value is currently active (for marker highlighting)
 * and manages pinned/frozen tooltip state for click-to-freeze feature.
 */
export interface TooltipContextValue {
  // Active X value (when tooltip is shown for a data point OR reference line)
  // Can be number (timestamps) or string (categories)
  activeXValue: number | string | null;
  setActiveXValue: (value: number | string | null) => void;

  // Pinned tooltip state for click-to-freeze feature
  /** Whether tooltip is currently pinned (frozen in place) */
  isPinned: boolean;
  /** SVG x-coordinate where crosshair stays fixed when pinned */
  pinnedXPosition: number | null;
  /** Data x-value for the pinned data point (number or string for categories) */
  pinnedXValue: number | string | null;
  /** Pre-rendered tooltip content to display when pinned */
  pinnedTooltipContent: ReactNode | null;
  /** Visx tooltip data (tooltipLeft, tooltipTop, tooltipData) frozen when pinned */
  pinnedVisxTooltipData: PinnedVisxTooltipData | null;
  /** Pin tooltip at the specified position with given content */
  pinTooltip: (data: PinnedTooltipData) => void;
  /** Unpin tooltip and return to hover mode */
  unpinTooltip: () => void;

  /** Whether the pointer is directly over this chart's container */
  isDirectlyHovered: boolean;
  /** Set by ChartCanvas on pointer enter/leave */
  setIsDirectlyHovered: (value: boolean) => void;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

export function TooltipProvider({ children }: { children: ReactNode }) {
  const [activeXValue, setActiveXValue] = useState<number | string | null>(null);

  // Pinned tooltip state
  const [isPinned, setIsPinned] = useState(false);
  const [pinnedXPosition, setPinnedXPosition] = useState<number | null>(null);
  const [pinnedXValue, setPinnedXValue] = useState<number | string | null>(null);
  const [pinnedTooltipContent, setPinnedTooltipContent] = useState<ReactNode | null>(null);
  const [pinnedVisxTooltipData, setPinnedVisxTooltipData] = useState<PinnedVisxTooltipData | null>(null);
  const [isDirectlyHovered, setIsDirectlyHovered] = useState(false);

  const pinTooltip = useCallback((data: PinnedTooltipData) => {
    setIsPinned(true);
    setPinnedXPosition(data.xPosition);
    setPinnedXValue(data.xValue);
    setPinnedTooltipContent(data.content);
    setPinnedVisxTooltipData(data.visxData);
  }, []);

  const unpinTooltip = useCallback(() => {
    setIsPinned(false);
    setPinnedXPosition(null);
    setPinnedXValue(null);
    setPinnedTooltipContent(null);
    setPinnedVisxTooltipData(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      activeXValue,
      setActiveXValue,
      isPinned,
      pinnedXPosition,
      pinnedXValue,
      pinnedTooltipContent,
      pinnedVisxTooltipData,
      pinTooltip,
      unpinTooltip,
      isDirectlyHovered,
      setIsDirectlyHovered,
    }),
    [
      activeXValue,
      isPinned,
      pinnedXPosition,
      pinnedXValue,
      pinnedTooltipContent,
      pinnedVisxTooltipData,
      pinTooltip,
      unpinTooltip,
      isDirectlyHovered,
    ],
  );

  return <TooltipContext.Provider value={contextValue}>{children}</TooltipContext.Provider>;
}

export function useTooltipContext() {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltipContext must be used within TooltipProvider');
  }
  return context;
}
